import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Collapse from '@mui/material/Collapse';
import { Feedback, Help, KeyboardArrowDown, KeyboardArrowRight, PowerSettingsNew, Settings, Wallet } from '@mui/icons-material';
import { useUserContext } from '../../context/user_context/UserContext';
import { useOrganizationContext } from '../../context/organization_context/OrganizationContext';
import { useNavigate } from 'react-router-dom';
import Logo from '../../resources/images/logo/bizismart.png';
import useNavBarItems from './NavBarItems';
import { useTheme as appTheme } from '../../context/theme_context/ThemeContext';
import { Button, ListItemIcon, useMediaQuery, FormControl, Select } from '@mui/material';
import { useAuthContext } from '../../context/auth/AuthContext';
import { useTheme } from '@mui/material/styles';
import ReportFeedbackModal from '../modals/ReportFeedbackModal';
import MenuIcon from '@mui/icons-material/Menu';
import { StringKeys } from '../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import usePermissions from '../../utils/permissions/UsePermissions';
import { Modules } from '../../utils/permissions/Modules';
import { PermissionTypes } from '../../utils/permissions/PermissionTypes';
import { useSignOutContext } from '../../context/sign_out_context/SignOutContext';
import Chatbot from '../chat_bot/ChatBot';
import SpanishFlag from '../../resources/images/flags/ES.svg'
import UnitedStatesFlag from '../../resources/images/flags/US.svg'

const CustomSelect = styled(Select)({
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiSelect-icon': {
    color: 'white',
  },
});

//Mobile popout sidebar

function MobileAppSidebar() {
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showBugModal, setShowBugModal] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { firebaseUser, userRole } = useUserContext();
  const { organizationRoles, organizationStripeInfo } = useOrganizationContext();
  const { hasPermission } = usePermissions()
  const { user } = useAuthContext()
  const { clearAndSignOut } = useSignOutContext()
  const { toggleThemeNoFirebaseUpdate } = appTheme();
  const navigate = useNavigate();
  const menuItems = useNavBarItems();
  const [showChatBot, setShowChatBot] = useState(false)
  const { t, i18n } = useTranslation();
  const defaultLanguage = 'en-US';
  const [language, setLanguage] = useState(() => {
    const currentLang = i18n.language || defaultLanguage;
    return currentLang.startsWith('en') ? 'en-US' : currentLang;
  });
  const [openItems, setOpenItems] = useState({});

  useEffect(() => {
    const savedOpenItems = JSON.parse(localStorage.getItem('openItems')) || {};
    setOpenItems(savedOpenItems);
  }, []);

  const saveOpenItems = (newOpenItems) => {
    localStorage.setItem('openItems', JSON.stringify(newOpenItems));
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const toggleDrawer = (open) => (event) => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleNavigation = (route) => {
    navigate(route);
    setDrawerOpen(false);
  };

  const handleSignOut = () => {
    toggleThemeNoFirebaseUpdate();
    handleCloseUserMenu();
    clearAndSignOut(navigate);
  };

  const changeLanguage = (lng) => {
    const normalizedLang = lng.startsWith('en') ? 'en-US' : lng;
    i18n.changeLanguage(normalizedLang);
    setLanguage(normalizedLang);
  };

  const handleClick = (label) => {
    setOpenItems((prev) => {
      const newOpenItems = { ...prev, [label]: !prev[label] };
      saveOpenItems(newOpenItems);  // Save open state to localStorage
      return newOpenItems;
    });
  };

  const drawerList = () => (
    <Box sx={{ width: 250 }} role="presentation">
      <List style={{ marginTop: '60px' }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.label}>
            <ListItem disablePadding>
              <ListItemButton onClick={() => item.route ? handleNavigation(item.route) : handleClick(item.label)}>
                {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                {item.subItems && (openItems[item.label] ? <KeyboardArrowDown /> : <KeyboardArrowRight />)}
                <ListItemText primary={item.translation_key === 'organization' ? item.label : t(item.translation_key)} sx={{ fontWeight: 'bold' }} />
              </ListItemButton>
            </ListItem>
            {item.subItems && (
              <Collapse in={openItems[item.label]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <ListItem key={subItem.label} disablePadding sx={{ pl: 3 }}>
                      <ListItemButton onClick={() => handleNavigation(subItem.route)}>
                        {subItem.icon && <ListItemIcon>{subItem.icon}</ListItemIcon>}
                        <ListItemText primary={t(subItem.translation_key)} sx={{ fontSize: '0.875rem' }} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
      <Divider />
      {/* Support ListItem */}
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => 
          {
            setShowChatBot(true)
            setDrawerOpen(false)
          }}>
            <ListItemIcon>
              <Help color="primary" />
            </ListItemIcon>
            <ListItemText primary={t(StringKeys.SUPPORT)} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => 
          {
            navigate('/settings')
          }}>
            <ListItemIcon>
              <Settings color="primary" />
            </ListItemIcon>
            <ListItemText primary={t(StringKeys.SETTINGS)} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );
  
  return (
    <>
      <AppBar position="fixed" style={{ background: 'black' }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {isDesktop && (
              <img
                alt="BiziSmart"
                src={Logo}
                width="30"
                height="30"
                onClick={() => navigate('/')}
                style={{ cursor: 'pointer', marginRight:"10px" }}
              />
            )}
            {!isDesktop && (
              <Box>
                <img
                  alt="BiziSmart"
                  src={Logo}
                  width="30"
                  height="30"
                  style={{ cursor: 'pointer' }}
                />
                {organizationStripeInfo?.onboarding_complete && (
                  <IconButton size="large" aria-label="open drawer" onClick={toggleDrawer(!drawerOpen)} color="inherit">
                    <MenuIcon />
                  </IconButton>
                )}
              </Box>
            )}
            <Typography
              variant="h6"
              sx={{ display: { xs: 'none', md: 'flex' }, fontWeight: 700, letterSpacing: '.3rem', color: 'inherit', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              BiziSmart
            </Typography>
            <Box sx={{ flexGrow: 1 }}></Box>
            <FormControl style={{ width: "140px" }} variant="outlined">
              <CustomSelect
                labelId="language-select-label"
                value={language}
                sx={{ color: "white", boxShadow: "none" }}
                onChange={(e) => changeLanguage(e.target.value)}
              >
                <MenuItem value="en-US">
                  <img alt="United States" src={UnitedStatesFlag} style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover', marginRight: '10px' }} />
                  {t(StringKeys.ENGLISH)}
                </MenuItem>
                <MenuItem value="es">
                  <img alt="Spain" src={SpanishFlag} style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover', marginRight: '10px' }} />
                  {t(StringKeys.SPANISH)}
                </MenuItem>
              </CustomSelect>
            </FormControl>
            <Box sx={{ flexGrow: 0 }}>
              {user ? (
                  <Tooltip title="Open settings">
                    <IconButton onClick={handleOpenUserMenu}>
                      <Avatar alt={firebaseUser.first_name} src={firebaseUser.profile_picture_url} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControl style={{ width: "140px" }} variant="outlined">
                    <CustomSelect
                      labelId="language-select-label"
                      value={language}
                      sx={{ color: "white", boxShadow: "none" }}
                      onChange={(e) => changeLanguage(e.target.value)}
                    >
                      <MenuItem value="en-US">
                        <img alt="United States" src={UnitedStatesFlag} style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover', marginRight: '10px' }} />
                        {t(StringKeys.ENGLISH)}
                      </MenuItem>
                      <MenuItem value="es">
                        <img alt="Spain" src={SpanishFlag} style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover', marginRight: '10px' }} />
                        {t(StringKeys.SPANISH)}
                      </MenuItem>
                    </CustomSelect>
                  </FormControl>
                  <Button sx={{textTransform:"none"}} onClick={() => navigate('/login')}>{t(StringKeys.LOG_IN)}</Button>
                </Box>
              )}
              <Menu anchorEl={anchorElUser} open={Boolean(anchorElUser)} onClose={handleCloseUserMenu}>
                {hasPermission(Modules.SUBSCRIPTION, PermissionTypes.READ) && (
                  <MenuItem onClick={() => { navigate('/subscription'); handleCloseUserMenu(); }}>
                    <Wallet/>
                    <Typography sx={{marginLeft:"10px"}}>{t(StringKeys.SUBSCRIPTION)}</Typography>
                  </MenuItem>
                )}
                <MenuItem onClick={() => {setShowBugModal(true); handleCloseUserMenu();}}>
                  <Feedback/>
                  <Typography sx={{marginLeft:"10px"}}>{t(StringKeys.FEEDBACK)}</Typography>
                </MenuItem>
                <MenuItem onClick={handleSignOut}>
                  <PowerSettingsNew />
                  <Typography sx={{marginLeft:"10px"}}>{t(StringKeys.LOG_OUT)}</Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      {
        organizationStripeInfo && organizationStripeInfo?.onboarding_complete && (
          <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
            {drawerList()}
          </Drawer>
        )
      }
      <Chatbot show={showChatBot} onClose={() => setShowChatBot(false)} />
      <ReportFeedbackModal show={showBugModal} onHide={() => setShowBugModal(false)} />
    </>
  );
}

export default MobileAppSidebar;
